import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
    Users,
    Search,
    User,
    FileText,
    Calendar,
    CreditCard,
    Clock,
    Plus,
    Trash2,
    UserCheck,
    UserX,
    Filter,
    ChevronDown,
    Dumbbell,
    Info,
    ArchiveRestore,
    AlertTriangle,
    ClipboardCheck,
    ShieldCheck,
    Shield,
    HeartPulse,
    ArrowLeft,
    GraduationCap
} from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { memberMatchesNameOrEmailSearch, normalizeMemberSearchInput } from '../../utils/memberSearchMatch';
import Popover from '../ui/Popover';
import LoadingSpinner from '../common/LoadingSpinner';
import { SkeletonList } from '../ui/Skeleton';
import ConfirmModal from '../ui/ConfirmModal';
import DeleteMemberModal from './DeleteMemberModal';
import Badge from '../ui/Badge';
import IOSModal from '../ui/IOSModal';
import CreateWorkoutModal from './CreateWorkoutModal';
import WorkoutDetailModal from './WorkoutDetailModal';
import EditWorkoutModal from './EditWorkoutModal';
import CreateSubscriptionModal from './CreateSubscriptionModal';
import AssignTemplateModal from './templates/AssignTemplateModal';
import SelectTemplateModal from './templates/SelectTemplateModal';
import ConvertToTemplateModal from './templates/ConvertToTemplateModal';
import EditClientProfileModal from './EditClientProfileModal';
import EditExtendedProfileModal from './EditExtendedProfileModal';
import ResetPasswordModal from './ResetPasswordModal';
import { formatExerciseDisplay } from '../../utils/exerciseFormatters';
import MemberCommunicationsTab from './MemberCommunicationsTab';
import MemberConsentsTab from './MemberConsentsTab';
import { ReactComponent as MemberInsuranceOkIcon } from '../../assets/icons/member-insurance-ok.svg';
import { ReactComponent as MemberViewStatisticsIcon } from '../../assets/icons/member-view-statistics.svg';
import { ReactComponent as MemberTabWorkoutsIcon } from '../../assets/icons/member-tab-workouts.svg';
import { ReactComponent as MemberTabHistoryIcon } from '../../assets/icons/member-tab-history.svg';
import { ReactComponent as MemberTabSubscriptionIcon } from '../../assets/icons/member-tab-subscription.svg';
import { ReactComponent as MemberTabContactIcon } from '../../assets/icons/member-tab-contact.svg';
import memberAvatarPlaceholder from '../../assets/images/member-avatar-placeholder.png';
import useMembersLayoutMode from '../../hooks/useMembersLayoutMode';
import MembersLayoutToggle from './MembersLayoutToggle';
import MembersTableView from './MembersTableView';
import MemberProfileTab from './MemberProfileTab';
import MemberAnamnesisTab from './MemberAnamnesisTab';
import MemberAccountTab from './MemberAccountTab';
import MemberHistoryTab from './memberDetail/MemberHistoryTab';
import MemberSubscriptionTab from './memberDetail/MemberSubscriptionTab';
import MemberWorkoutsTab from './memberDetail/MemberWorkoutsTab';
import MemberCoursesTab from './memberDetail/MemberCoursesTab';

export default function MembersManager() {
    const { getAuthenticatedApi, isGym, isTrainer } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { layoutMode, isTableLayout, setLayoutMode } = useMembersLayoutMode();
    const [members, setMembers] = useState([]);
    const [selectedMember, setSelectedMember] = useState(null);
    const [loading, setLoading] = useState(true);
    const [listLoading, setListLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('profile');
    // Trigger per forzare il refresh della tab "Schede Allenamento" senza reload pagina.
    const [workoutsRefreshKey, setWorkoutsRefreshKey] = useState(0);
    // Stesso modello per tab Abbonamento (niente window.location.reload → resta su cliente + gestione clienti).
    const [subscriptionRefreshKey, setSubscriptionRefreshKey] = useState(0);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [subscriptionModalMode, setSubscriptionModalMode] = useState('create');
    const [subscriptionToEdit, setSubscriptionToEdit] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingWorkout, setEditingWorkout] = useState(null);
    const [showWorkoutDetailModal, setShowWorkoutDetailModal] = useState(false);
    const [selectedWorkout, setSelectedWorkout] = useState(null);
    const [showAssignTemplateModal, setShowAssignTemplateModal] = useState(false);
    const [showSelectTemplateModal, setShowSelectTemplateModal] = useState(false);
    const [selectedTemplateForAssign, setSelectedTemplateForAssign] = useState(null);
    const [showConvertToTemplateModal, setShowConvertToTemplateModal] = useState(false);
    const [workoutToConvert, setWorkoutToConvert] = useState(null);
    const [showEditProfileModal, setShowEditProfileModal] = useState(false);
    const [showExtendedProfileModal, setShowExtendedProfileModal] = useState(false);
    const [extendedProfileSection, setExtendedProfileSection] = useState(null);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [selectedMemberForReset, setSelectedMemberForReset] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null); // { type, memberId, memberName }
    const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 }); // Per animazione iOS
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [memberToDelete, setMemberToDelete] = useState(null); // { id, name, email }
    const [deleting, setDeleting] = useState(false);
    const [statusView, setStatusView] = useState('active'); // 'active' | 'trainer' | 'inactive' (trainer solo se isGym)
    const [statusLoadingId, setStatusLoadingId] = useState(null);
    // WEBAPP-032: filtri sotto ricerca (solo desktop, non mobile)
    const [filterSubscription, setFilterSubscription] = useState(''); // '' | 'active' | 'expired' | 'no_subscription'
    const [filterLastWorkout, setFilterLastWorkout] = useState(''); // '' | 'never' | '7' | '7_30' | '30_plus'
    // WEBAPP-034: paginazione lista clienti (15 per pagina)
    const [membersPage, setMembersPage] = useState(0);
    const [totalMembers, setTotalMembers] = useState(0);
    // Conteggi tab Clienti / Trainer / Rimossi: devono essere globali (total API), non dalla sola pagina corrente -
    // altrimenti sulla tab "rimossi" `members` contiene solo inattivi e i badge attivi risultano 0 (WEBAPP-034).
    const [memberTabTotals, setMemberTabTotals] = useState({
        clients: 0,
        trainers: 0,
        inactive: 0
    });

    const MEMBERS_PAGE_SIZE = 50; // WEBAPP-034
    const isMobile = useMediaQuery('(max-width: 1023px)'); // Allineato a sidebar: sotto 1024px layout lista/dettaglio full screen (WEBAPP-033)
    const isMobileRef = React.useRef(isMobile);
    const isTableLayoutRef = React.useRef(isTableLayout);
    // Ref scroll lista clienti: definite subito (evita HMR parziali con ref={...} senza const).
    const membersListScrollDesktopRef = React.useRef(null);
    const membersListScrollMobileRef = React.useRef(null);
    useEffect(() => {
        isMobileRef.current = isMobile;
    }, [isMobile]);
    useEffect(() => {
        isTableLayoutRef.current = isTableLayout;
    }, [isTableLayout]);

    // Preserva il cliente selezionato durante i refresh (evita di "saltare" al primo elemento).
    // Important: usiamo una ref perché `fetchMembers` non dipende da `selectedMember`.
    const selectedMemberIdRef = useRef(null);
    const hasLoadedOnceRef = useRef(false);
    /** WEBAPP-4: `memberId` nella querystring (numero). */
    const memberIdFromSearchRef = useRef(null);
    /** Evita fetch ripetuto per lo stesso id finché non cambia l'URL. */
    const urlMemberFetchedIdRef = useRef(null);
    const urlMemberMissingToastIdRef = useRef(null);
    /** Evita che risposte HTTP vecchie (digitazione rapida) sovrascrivano la lista corrente */
    const fetchMembersRequestIdRef = useRef(0);
    useEffect(() => {
        selectedMemberIdRef.current = selectedMember?.id ?? null;
    }, [selectedMember?.id]);

    useEffect(() => {
        if (selectedMember?.id != null) {
            setActiveTab('profile');
        }
    }, [selectedMember?.id]);

    const scrollSelectedMemberCardIntoView = useCallback((memberId) => {
        if (memberId == null) return;
        const roots = [membersListScrollDesktopRef.current, membersListScrollMobileRef.current].filter(Boolean);
        for (const root of roots) {
            const el = root.querySelector(`[data-member-id="${String(memberId)}"]`);
            if (el) {
                el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
                return;
            }
        }
    }, []);

    useLayoutEffect(() => {
        const params = new URLSearchParams(location.search || '');
        const rawId = params.get('memberId');
        const n = rawId != null && rawId !== '' && !Number.isNaN(Number(rawId)) ? Number(rawId) : null;
        memberIdFromSearchRef.current = n;
    }, [location.search]);

    useEffect(() => {
        urlMemberFetchedIdRef.current = null;
        urlMemberMissingToastIdRef.current = null;
    }, [location.search, membersPage, filterSubscription, filterLastWorkout, searchTerm, statusView]);

    const syncMemberIdInUrl = useCallback((memberId) => {
        const nextId = memberId != null && memberId !== '' && !Number.isNaN(Number(memberId))
            ? Number(memberId)
            : null;
        memberIdFromSearchRef.current = nextId;
        const params = new URLSearchParams(location.search);
        if (nextId != null) {
            params.set('memberId', String(nextId));
        } else {
            params.delete('memberId');
        }
        const qs = params.toString();
        const nextSearch = qs ? `?${qs}` : '';
        if ((location.search || '') === nextSearch) return;
        navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }, [location.pathname, location.search, navigate]);

    const getMemberStatus = useCallback((member) => member?.membership_status || 'active', []);

    const activeMembers = useMemo(
        () => members.filter((member) => getMemberStatus(member) === 'active'),
        [members, getMemberStatus]
    );
    const inactiveMembers = useMemo(
        () => members.filter((member) => getMemberStatus(member) === 'inactive'),
        [members, getMemberStatus]
    );
    // WEBAPP-029: separazione Clienti / Trainer (solo per gym)
    const clientMembers = useMemo(
        () => members.filter((m) => getMemberStatus(m) === 'active' && m.role_in_gym === 'member'),
        [members, getMemberStatus]
    );
    const trainerMembers = useMemo(
        () => members.filter((m) => getMemberStatus(m) === 'active' && m.role_in_gym === 'trainer'),
        [members, getMemberStatus]
    );

    const fetchMemberTabTotals = useCallback(async () => {
        if (!isGym()) return;
        try {
            const api = getAuthenticatedApi();
            const buildParams = (status, roleInGym) => {
                const p = new URLSearchParams({
                    action: 'list',
                    status,
                    limit: '1',
                    offset: '0'
                });
                if (status === 'active' && roleInGym) {
                    p.set('role_in_gym', roleInGym);
                }
                if (filterSubscription) p.set('subscription_status', filterSubscription);
                if (filterLastWorkout) p.set('last_workout', filterLastWorkout);
                return p;
            };
            const [resClients, resTrainers, resInactive] = await Promise.all([
                api.get(`/gym_members.php?${buildParams('active', 'member')}`),
                api.get(`/gym_members.php?${buildParams('active', 'trainer')}`),
                api.get(`/gym_members.php?${buildParams('inactive', null)}`)
            ]);
            const n = (d) => (d?.success && typeof d.total === 'number' ? d.total : 0);
            setMemberTabTotals({
                clients: n(resClients.data),
                trainers: n(resTrainers.data),
                inactive: n(resInactive.data)
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Errore caricamento conteggi tab membri:', e);
        }
    }, [getAuthenticatedApi, isGym, filterSubscription, filterLastWorkout]);

    /** Badge lista: active | expired (ultimo periodo terminato) | no_subscription (mai avuto). Allineato a list_badge_subscription_status dalla API. */
    const subscriptionListBadgeFromClientResponse = useCallback((data) => {
        if (!data) return 'no_subscription';
        if (data.success && data.subscription) return 'active';
        if (data.list_badge_subscription_status === 'expired') return 'expired';
        return 'no_subscription';
    }, []);

    /** Dopo crea/modifica abbonamento: aggiorna badge in lista e dettaglio senza fetchMembers() né reload. */
    const refreshMemberSubscriptionRow = useCallback(
        async (clientId) => {
            if (!clientId) return;
            try {
                const api = getAuthenticatedApi();
                const subscriptionResponse = await api.get(
                    `/client_subscription_management.php?action=client_subscription&client_id=${clientId}`
                );
                const payload = subscriptionResponse.data;
                const subscription = payload?.success ? payload.subscription : null;
                const subscriptionStatus = subscriptionListBadgeFromClientResponse(payload);
                setMembers((prev) =>
                    prev.map((m) =>
                        m.id === clientId ? { ...m, subscription, subscriptionStatus } : m
                    )
                );
                setSelectedMember((prev) =>
                    prev && prev.id === clientId ? { ...prev, subscription, subscriptionStatus } : prev
                );
            } catch {
                /* lista si allinea al prossimo fetchMembers */
            }
        },
        [getAuthenticatedApi, subscriptionListBadgeFromClientResponse]
    );

    /** Abbonamento, assicurazione e stati dashboard (stesso output della lista paginata). */
    const attachMembersMetadata = useCallback(
        async (api, membersData) => {
            const membersWithSubscriptionStatus = await Promise.all(
                membersData.map(async (member) => {
                    let subscription = null;
                    let subscriptionStatus = null;
                    let insuranceStatus = null;

                    if (member.role_in_gym === 'member') {
                        try {
                            const subscriptionResponse = await api.get(
                                `/client_subscription_management.php?action=client_subscription&client_id=${member.id}`
                            );
                            const subPayload = subscriptionResponse.data;
                            subscription = subPayload?.success ? subPayload.subscription : null;
                            subscriptionStatus = subscriptionListBadgeFromClientResponse(subPayload);
                        } catch (err) {
                            if (err.response && err.response.status === 403) {
                                subscription = null;
                                subscriptionStatus = 'no_subscription';
                            } else {
                                // eslint-disable-next-line no-console
                                console.error(`Errore caricamento abbonamento per membro ${member.id}:`, err);
                                subscription = null;
                                subscriptionStatus = 'no_subscription';
                            }
                        }
                        try {
                            const insRes = await api.get(
                                `/client_insurance_management.php?action=client_insurance&client_id=${member.id}`
                            );
                            const insurance = insRes.data.success ? insRes.data.insurance : null;
                            if (!insurance) {
                                insuranceStatus = 'no_assicurazione';
                            } else if (insurance.status === 'expired') {
                                insuranceStatus = 'scaduta';
                            } else {
                                insuranceStatus = 'ok';
                            }
                        } catch (err) {
                            if (err.response && err.response.status !== 403) {
                                // eslint-disable-next-line no-console
                                console.error(`Errore caricamento assicurazione per membro ${member.id}:`, err);
                            }
                            insuranceStatus = 'no_assicurazione';
                        }
                        return {
                            ...member,
                            subscription,
                            subscriptionStatus,
                            insuranceStatus
                        };
                    }
                    return {
                        ...member,
                        subscription: null,
                        subscriptionStatus: null,
                        insuranceStatus: null
                    };
                })
            );

            let membersWithStati = membersWithSubscriptionStatus;
            try {
                const statiRes = await api.get('/gym_dashboard.php?action=stati_clienti');
                if (statiRes.data?.success && statiRes.data.stati) {
                    const stati = statiRes.data.stati;
                    membersWithStati = membersWithSubscriptionStatus.map((m) => {
                        if (m.role_in_gym !== 'member' || !stati[m.id]) return m;
                        return {
                            ...m,
                            stato_cliente: stati[m.id].stato,
                            nuovo_cliente: stati[m.id].nuovo_cliente === true,
                            ultimo_allenamento: stati[m.id].ultimo_allenamento ?? m.ultimo_allenamento ?? null
                        };
                    });
                }
            } catch {
                /* stati opzionali */
            }
            return membersWithStati;
        },
        [subscriptionListBadgeFromClientResponse]
    );

    const fetchMembers = useCallback(async () => {
        const requestId = ++fetchMembersRequestIdRef.current;
        const isStale = () => requestId !== fetchMembersRequestIdRef.current;
        try {
            if (hasLoadedOnceRef.current) {
                setListLoading(true);
            } else {
                setLoading(true);
            }
            const api = getAuthenticatedApi();
            
            let membersData = [];
            let total = 0;
            if (isTrainer()) {
                // Per trainer: nessuna paginazione (trainer_data) con supporto ricerca lato API (WEBAPP-036)
                const params = new URLSearchParams({ action: 'clients' });
                const qSearch = normalizeMemberSearchInput(searchTerm);
                if (qSearch.length >= 2) {
                    params.set('search', qSearch);
                }
                const clientsResponse = await api.get(`/trainer_data.php?${params.toString()}`);
                membersData = clientsResponse.data;
            } else {
                // WEBAPP-034: gym - una sola chiamata paginata (50/pagina) con filtri e role
                const status = statusView === 'inactive' ? 'inactive' : 'active';
                const params = new URLSearchParams({
                    action: 'list',
                    status,
                    limit: String(MEMBERS_PAGE_SIZE),
                    offset: String(membersPage * MEMBERS_PAGE_SIZE)
                });
                if (status === 'active') {
                    if (statusView === 'trainer') params.set('role_in_gym', 'trainer');
                    else params.set('role_in_gym', 'member');
                }
                if (filterSubscription) params.set('subscription_status', filterSubscription);
                if (filterLastWorkout) params.set('last_workout', filterLastWorkout);
                // WEBAPP-036: ricerca su tutti i membri (non solo pagina corrente), token ordine-indipendente (API)
                const qSearch = normalizeMemberSearchInput(searchTerm);
                if (qSearch.length >= 2) {
                    params.set('search', qSearch);
                }
                const listRes = await api.get(`/gym_members.php?${params.toString()}`);
                const data = listRes.data;
                if (data?.success && Array.isArray(data.members)) {
                    membersData = data.members.filter(m => m.role_in_gym !== 'manager');
                    total = typeof data.total === 'number' ? data.total : membersData.length;
                }
            }
            
            const membersWithStati = await attachMembersMetadata(api, membersData);

            if (isStale()) return;

            const urlMid = memberIdFromSearchRef.current;
            if (urlMid != null && !membersWithStati.some((m) => Number(m.id) === urlMid)) {
                urlMemberFetchedIdRef.current = null;
            }

            setMembers(membersWithStati);
            if (isGym()) setTotalMembers(total);
            
            // Preferisci membro da query `memberId` (deep link) se è in questa pagina; altrimenti desktop preserva selezione / primo.
            if (membersWithStati.length > 0) {
                const urlId = memberIdFromSearchRef.current;
                const urlPick = urlId != null ? membersWithStati.find((m) => Number(m.id) === urlId) : null;
                if (isMobileRef.current || isTableLayoutRef.current) {
                    setSelectedMember(urlPick ?? null);
                } else {
                    const preservedId = selectedMemberIdRef.current;
                    const preservedMember = preservedId
                        ? membersWithStati.find((m) => m.id === preservedId)
                        : null;
                    setSelectedMember(urlPick ?? preservedMember ?? membersWithStati[0]);
                }
            } else {
                setSelectedMember(null);
            }
            
            setError(null);
        } catch (err) {
            if (!isStale()) {
                // eslint-disable-next-line no-console
                console.error('Errore nel caricamento membri:', err);
                setError(t('gym.membersManager.errorLoadingMembers'));
            }
        } finally {
            if (!isStale()) {
                setLoading(false);
                setListLoading(false);
                hasLoadedOnceRef.current = true;
            }
        }
    }, [getAuthenticatedApi, isTrainer, isGym, statusView, membersPage, filterSubscription, filterLastWorkout, searchTerm, attachMembersMetadata]);

    /** WEBAPP-4: carica un singolo membro per id (lista paginata può non contenerlo). */
    const fetchMemberByUserIdForUrl = useCallback(
        async (userId) => {
            if (!userId || isTrainer()) return null;
            const api = getAuthenticatedApi();
            for (const membershipStatus of ['active', 'inactive']) {
                const params = new URLSearchParams({
                    action: 'list',
                    status: membershipStatus,
                    limit: '1',
                    offset: '0',
                    user_id: String(userId)
                });
                const listRes = await api.get(`/gym_members.php?${params.toString()}`);
                const data = listRes.data;
                if (!data?.success || !Array.isArray(data.members) || data.members.length === 0) continue;
                const rows = data.members.filter((m) => m.role_in_gym !== 'manager');
                if (rows.length === 0) continue;
                const enriched = await attachMembersMetadata(api, rows);
                return enriched[0] || null;
            }
            return null;
        },
        [getAuthenticatedApi, isTrainer, attachMembersMetadata]
    );

    useEffect(() => {
        const mid = memberIdFromSearchRef.current;
        if (mid == null || !isGym() || isTrainer()) return;

        const inList = members.some((m) => Number(m.id) === mid);
        if (inList) {
            const found = members.find((m) => Number(m.id) === mid);
            if (found) {
                setSelectedMember((prev) => (prev?.id === found.id ? prev : found));
                urlMemberFetchedIdRef.current = mid;
                requestAnimationFrame(() => scrollSelectedMemberCardIntoView(mid));
            }
            return;
        }

        if (loading || listLoading) return;
        if (urlMemberFetchedIdRef.current === mid) return;

        let cancelled = false;
        (async () => {
            try {
                const enriched = await fetchMemberByUserIdForUrl(mid);
                if (cancelled) return;
                if (enriched) {
                    urlMemberFetchedIdRef.current = mid;
                    setMembers((prev) => {
                        if (prev.some((m) => Number(m.id) === Number(enriched.id))) return prev;
                        return [enriched, ...prev];
                    });
                    setSelectedMember(enriched);
                    requestAnimationFrame(() => scrollSelectedMemberCardIntoView(enriched.id));
                } else {
                    urlMemberFetchedIdRef.current = mid;
                    if (urlMemberMissingToastIdRef.current !== mid) {
                        urlMemberMissingToastIdRef.current = mid;
                        toast.error(t('gym.membersManager.memberFromUrlNotFound'));
                    }
                    if (memberIdFromSearchRef.current === mid) {
                        syncMemberIdInUrl(null);
                    }
                }
            } catch {
                if (!cancelled) {
                    urlMemberFetchedIdRef.current = mid;
                    if (urlMemberMissingToastIdRef.current !== mid) {
                        urlMemberMissingToastIdRef.current = mid;
                        toast.error(t('gym.membersManager.memberFromUrlNotFound'));
                    }
                    if (memberIdFromSearchRef.current === mid) {
                        syncMemberIdInUrl(null);
                    }
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        location.search,
        members,
        loading,
        listLoading,
        isGym,
        isTrainer,
        fetchMemberByUserIdForUrl,
        scrollSelectedMemberCardIntoView,
        syncMemberIdInUrl,
        t
    ]);

    const handleSearchTermChange = useCallback(
        (e) => {
            setSearchTerm(e.target.value);
            if (isGym()) setMembersPage(0);
        },
        [isGym]
    );

    const handleSelectMember = useCallback((member) => {
        setSelectedMember(member);
        if (isTableLayoutRef.current) {
            syncMemberIdInUrl(member?.id ?? null);
        }
    }, [syncMemberIdInUrl]);

    const handleClearSelectedMember = useCallback(() => {
        setSelectedMember(null);
        if (isTableLayoutRef.current) {
            syncMemberIdInUrl(null);
        }
    }, [syncMemberIdInUrl]);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    useEffect(() => {
        fetchMemberTabTotals();
    }, [fetchMemberTabTotals]);

    const updateMemberStatus = useCallback(async (memberId, newStatus, options = {}) => {
        try {
            setStatusLoadingId(memberId);
            const api = getAuthenticatedApi();
            await api.put(`/gym_members.php?user_id=${memberId}`, { status: newStatus });

            setMembers(prev => prev.map(member => 
                member.id === memberId 
                    ? { ...member, membership_status: newStatus }
                    : member
            ));

            setSelectedMember(prev => (prev?.id === memberId ? { ...prev, membership_status: newStatus } : prev));

            if (options.successMessage) {
                toast.success(options.successMessage);
            } else {
                toast.success(
                    newStatus === 'active'
                        ? t('gym.membersManager.toasts.reactivated')
                        : t('gym.membersManager.toasts.deactivated')
                );
            }
            if (isGym()) {
                fetchMemberTabTotals();
            }
        } catch (err) {
            console.error('Errore nel cambio stato:', err);
            toast.error(t('gym.membersManager.errorStatusChange'));
        } finally {
            setStatusLoadingId(null);
        }
    }, [getAuthenticatedApi, t, isGym, fetchMemberTabTotals]);

    const handleStatusToggle = async (memberId, currentStatus) => {
        const nextStatus = currentStatus === 'active' ? 'inactive' : 'active';
        return updateMemberStatus(memberId, nextStatus);
    };

    const handleReactivateMember = async (memberId) => {
        try {
            setStatusLoadingId(memberId);
            const api = getAuthenticatedApi();
            const response = await api.put(`/gym_members.php?user_id=${memberId}&action=reactivate`);
            
            if (response.data.success) {
                // Aggiorna lo stato del membro nella lista
                setMembers(prev => prev.map(member => 
                    member.id === memberId 
                        ? { ...member, membership_status: 'active', left_at: null }
                        : member
                ));
                
                // Aggiorna anche il membro selezionato
                if (selectedMember?.id === memberId) {
                    setSelectedMember(prev => ({
                        ...prev,
                        membership_status: 'active',
                        left_at: null
                    }));
                }
                
                toast.success(t('gym.membersManager.toasts.reactivated'));
                
                // Ricarica i membri per avere dati aggiornati
                await fetchMembers();
                if (isGym()) await fetchMemberTabTotals();
            } else {
                throw new Error(response.data.error || 'Errore nella riattivazione');
            }
        } catch (err) {
            console.error('Errore nella riattivazione:', err);
            toast.error(err.response?.data?.error || t('gym.membersManager.errorStatusChange'));
        } finally {
            setStatusLoadingId(null);
        }
    };

    const handleRemoveMember = async (memberId, memberName) => {
        // Trova il membro completo per email
        const member = members.find(m => m.id === memberId);
        if (member) {
            setMemberToDelete({
                id: memberId,
                name: memberName || member.name,
                email: member.email
            });
            setShowDeleteModal(true);
        }
    };
    
    const executeDeleteMember = async (deleteMode) => {
        if (!memberToDelete) return;

        setDeleting(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.delete(
                `/gym_members.php?user_id=${memberToDelete.id}&action=delete&mode=${deleteMode}`
            );

            if (Number(memberIdFromSearchRef.current) === Number(memberToDelete.id)) {
                syncMemberIdInUrl(null);
            }
            
            if (deleteMode === 'soft') {
                // Per soft delete, aggiorna lo stato invece di rimuovere
                setMembers(prev => prev.map(member => 
                    member.id === memberToDelete.id 
                        ? { ...member, membership_status: 'inactive', left_at: new Date().toISOString() }
                        : member
                ));
                
                // Se il membro era selezionato, deseleziona
                if (selectedMember?.id === memberToDelete.id) {
                    setSelectedMember(null);
                }
                
                toast.success('Membro disabilitato con successo (storico mantenuto)');
            } else {
                // Per hard delete, rimuovi dalla lista
                setMembers(prev => prev.filter(member => member.id !== memberToDelete.id));
                
                // Se il membro cancellato era selezionato, seleziona il primo disponibile
                if (selectedMember?.id === memberToDelete.id) {
                    const remainingMembers = members.filter(member => member.id !== memberToDelete.id);
                    setSelectedMember(remainingMembers.length > 0 ? remainingMembers[0] : null);
                }
                
                toast.success('Membro cancellato definitivamente');
            }
            
            setShowDeleteModal(false);
            setMemberToDelete(null);
            
            // Ricarica i membri per avere dati aggiornati
            await fetchMembers();
            if (isGym()) await fetchMemberTabTotals();
            
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('Errore nella cancellazione:', err);
            const errorMessage = err.response?.data?.error || 'Errore nella cancellazione del membro';
            toast.error(errorMessage);
        } finally {
            setDeleting(false);
        }
    };

    const handleResetPassword = async (memberId, memberName) => {
        const member = members.find(m => m.id === memberId);
        setSelectedMemberForReset(member);
        setShowResetPasswordModal(true);
    };

    const handleMemberPatch = useCallback((patch) => {
        if (!patch?.id) return;
        setMembers((prev) => prev.map((m) => (m.id === patch.id ? { ...m, ...patch } : m)));
        setSelectedMember((prev) => (prev && prev.id === patch.id ? { ...prev, ...patch } : prev));
    }, []);

    const openExtendedProfileSection = useCallback((section) => {
        setExtendedProfileSection(section);
        setShowExtendedProfileModal(true);
    }, []);

    const closeExtendedProfileModal = useCallback(() => {
        setShowExtendedProfileModal(false);
        setExtendedProfileSection(null);
    }, []);

    const handleResetPasswordConfirm = async (memberId, memberName) => {
        try {
            const api = getAuthenticatedApi();
            const response = await api.post('/gym_members.php?action=reset_password', {
                user_id: memberId
            });
            
            if (response.data.success) {
                // Aggiorna solo il membro specifico nella lista per mantenere la selezione
                setMembers(prev => prev.map(member => 
                    member.id === memberId 
                        ? { ...member, temp_password: response.data.temp_password }
                        : member
                ));
                
                // Aggiorna anche il membro selezionato se è lo stesso
                if (selectedMember && selectedMember.id === memberId) {
                    setSelectedMember(prev => ({
                        ...prev,
                        temp_password: response.data.temp_password
                    }));
                }
                
                // Ritorna il risultato per il modal
                return response.data;
            } else {
                throw new Error(response.data.error || 'Errore nel reset della password');
            }
        } catch (err) {
            console.error('Errore nel reset password:', err);
            setError(err.response?.data?.error || 'Errore nel reset della password');
            throw err;
        }
    };

    const handleShowWorkoutDetail = (workout) => {
        setSelectedWorkout(workout);
        setShowWorkoutDetailModal(true);
    };

    const visibleMembers = isGym()
        ? (statusView === 'inactive' ? inactiveMembers : statusView === 'trainer' ? trainerMembers : clientMembers)
        : (statusView === 'inactive' ? inactiveMembers : activeMembers);

    // useMemo: evita nuovo array a ogni render (es. dopo refresh schede) così l'useEffect sulla selezione
    // non si riattiva a vuoto e la lista non "salta" visivamente in cima.
    const filteredMembers = useMemo(() => {
        // WEBAPP-032/034: filtri. Se gym con paginazione, l'API ha già applicato subscription/last_workout: non riapplicarli client-side (evita "11 di 12" per dati/formato ultimo_allenamento).
        const serverFiltered = isGym() && totalMembers > 0 && (filterSubscription || filterLastWorkout);
        const filteredBySubscription = !filterSubscription
            ? visibleMembers
            : serverFiltered
                ? visibleMembers
                : visibleMembers.filter((m) => m.role_in_gym !== 'member' || m.subscriptionStatus === filterSubscription);
        const filteredByLastWorkout = !filterLastWorkout
            ? filteredBySubscription
            : serverFiltered
                ? filteredBySubscription
                : filteredBySubscription.filter((m) => {
                    if (m.role_in_gym !== 'member') return true;
                    const last = m.ultimo_allenamento ? new Date(m.ultimo_allenamento) : null;
                    const now = new Date();
                    if (filterLastWorkout === 'never') return !last;
                    if (!last) return false;
                    const daysAgo = (now.getTime() - last.getTime()) / (24 * 60 * 60 * 1000);
                    if (filterLastWorkout === '7') return daysAgo <= 7;
                    if (filterLastWorkout === '7_30') return daysAgo > 7 && daysAgo <= 30;
                    if (filterLastWorkout === '30_plus') return daysAgo > 30;
                    return true;
                });
        return filteredByLastWorkout.filter((member) =>
            memberMatchesNameOrEmailSearch(member, searchTerm)
        );
    }, [
        visibleMembers,
        isGym,
        totalMembers,
        filterSubscription,
        filterLastWorkout,
        searchTerm
    ]);

    useEffect(() => {
        const targetList = filteredMembers;
        if (targetList.length === 0) {
            if (selectedMember && !targetList.some(member => member.id === selectedMember.id)) {
                setSelectedMember(null);
            }
            return;
        }
        const hasSelectedMember = selectedMember && targetList.some(member => member.id === selectedMember.id);
        if (!hasSelectedMember) {
            // Su mobile / layout tabella: nessuna preselezione; su desktop classico seleziona il primo (WEBAPP-033)
            if (!isMobile && !isTableLayout) {
                setSelectedMember(targetList[0]);
            } else {
                setSelectedMember(null);
            }
        }
    }, [statusView, visibleMembers, filteredMembers, selectedMember, isMobile, isTableLayout, filterSubscription, filterLastWorkout]);

    // Dopo crea/modifica scheda: riporta in vista la card nella sidebar (non modifica membersPage / paginazione).
    useLayoutEffect(() => {
        if (workoutsRefreshKey === 0) return;
        const id = selectedMember?.id;
        if (id == null) return;
        requestAnimationFrame(() => {
            scrollSelectedMemberCardIntoView(id);
        });
    }, [workoutsRefreshKey, selectedMember?.id, scrollSelectedMemberCardIntoView]);

    const canModifyMembers = isGym();
    const canReactivateMembers = isGym() || isTrainer();

    const renderDesktopStatusTabs = () => (
        <>
            {isGym() ? (
                <>
                    <button
                        type="button"
                        onClick={() => { setStatusView('active'); setMembersPage(0); }}
                        className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-xl border transition-colors dark:px-5 dark:py-2.5 ${
                            statusView === 'active'
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                                : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-blue-400 dark:hover:bg-white/5'
                        }`}
                    >
                        <Users className="dark:hidden shrink-0" size={18} />
                        <span className="font-semibold text-sm dark:font-open-sans">{t('gym.membersManager.statusTabs.clients')}</span>
                        {statusView === 'active' ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-indigo-900/50 dark:text-white">{memberTabTotals.clients}</span>
                        ) : (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{memberTabTotals.clients}</span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={() => { setStatusView('trainer'); setMembersPage(0); }}
                        className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-xl border transition-colors dark:px-5 dark:py-2.5 ${
                            statusView === 'trainer'
                                ? 'bg-blue-600 text-white border-blue-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                                : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-blue-400 dark:hover:bg-white/5'
                        }`}
                    >
                        <UserCheck className="dark:hidden shrink-0" size={18} />
                        <span className="font-semibold text-sm dark:font-open-sans">{t('gym.membersManager.statusTabs.trainers')}</span>
                        {statusView === 'trainer' ? (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-indigo-900/50 dark:text-white">{memberTabTotals.trainers}</span>
                        ) : (
                            <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{memberTabTotals.trainers}</span>
                        )}
                    </button>
                </>
            ) : (
                <button
                    type="button"
                    onClick={() => { setStatusView('active'); setMembersPage(0); }}
                    className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-xl border transition-colors dark:px-5 dark:py-2.5 ${
                        statusView === 'active'
                            ? 'bg-blue-600 text-white border-blue-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                            : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-blue-400 dark:hover:bg-white/5'
                    }`}
                >
                    <Users className="dark:hidden shrink-0" size={18} />
                    <span className="font-semibold text-sm dark:font-open-sans">{t('gym.membersManager.statusTabs.clients')}</span>
                    {statusView === 'active' ? (
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-indigo-900/50 dark:text-white">{activeMembers.length}</span>
                    ) : (
                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{activeMembers.length}</span>
                    )}
                </button>
            )}
            <button
                type="button"
                onClick={() => { setStatusView('inactive'); setMembersPage(0); }}
                className={`flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-xl border transition-colors dark:px-5 dark:py-2.5 ${
                    statusView === 'inactive'
                        ? 'bg-amber-600 text-white border-amber-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                        : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-amber-400 dark:hover:bg-white/5'
                }`}
            >
                <ArchiveRestore className="dark:hidden shrink-0" size={18} />
                <span className="font-semibold text-sm dark:font-open-sans">{t('gym.membersManager.statusTabs.inactive')}</span>
                {statusView === 'inactive' ? (
                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-900 dark:bg-indigo-900/50 dark:text-white">{isGym() ? memberTabTotals.inactive : inactiveMembers.length}</span>
                ) : (
                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{isGym() ? memberTabTotals.inactive : inactiveMembers.length}</span>
                )}
            </button>
            <Popover
                placement="bottom"
                title={t('gym.membersManager.statusInfo.title')}
                trigger={
                    <button
                        type="button"
                        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-[#bca8d1] bg-[#e3deef] transition-colors hover:brightness-[0.97] dark:border-white/20 dark:bg-gray-800/55 dark:hover:bg-gray-700"
                        title={t('gym.membersManager.statusInfo.title')}
                    >
                        <Info className="text-[#372d83] dark:text-gray-300" size={18} />
                    </button>
                }
                content={
                    <div className="space-y-3">
                        <p>{t('gym.membersManager.statusInfo.description')}</p>
                        <p className="text-sm text-[#3a3a3a]/80 dark:text-white/75">
                            {t('gym.membersManager.statusInfo.tip')}
                        </p>
                    </div>
                }
            />
        </>
    );

    const tableLayoutEmptyTitle = statusView === 'inactive'
        ? t('gym.membersManager.emptyState.inactive')
        : statusView === 'trainer'
            ? t('gym.membersManager.emptyState.noTrainers')
            : t('gym.membersManager.emptyState.search');
    const tableLayoutEmptyDescription = statusView === 'inactive'
        ? t('gym.membersManager.emptyState.inactiveDescription')
        : statusView === 'trainer'
            ? t('gym.membersManager.emptyState.noTrainersDescription')
            : t('gym.membersManager.emptyState.searchDescription');

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700">{error}</p>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 dark:bg-gray-900 h-screen flex flex-col overflow-hidden">
            {/* Su mobile con dettaglio aperto: nessuna barra separata; Indietro è nel header del dettaglio a sinistra dell'avatar (WEBAPP-033) */}
            {selectedMember && (isMobile || isTableLayout) ? null : (
            <>
            {/* Header - Fisso in alto (nascosto su mobile quando si vede il dettaglio). Su mobile compatto per lasciare spazio alla lista (WEBAPP-033). */}
            <div className="flex-shrink-0">
                <div
                    className={`${isMobile ? 'px-3 pt-2 pb-2 dark:pb-0' : ''} dark:bg-gradient-to-r dark:from-gray-950 dark:via-gray-900 dark:to-gray-900`}
                >
                    {isMobile ? (
                        /* Mobile: titolo e tab sulla stessa riga (WEBAPP-033) */
                        <div className="flex items-center gap-2 flex-nowrap min-h-[44px]">
                            <div className="flex items-center flex-shrink-0 min-w-0">
                                <h1 className="font-bold text-gray-900 dark:text-white dark:font-open-sans text-lg truncate">
                                    {isTrainer() ? t('gym.membersManager.trainerTitle') : t('gym.membersManager.title')}
                                </h1>
                            </div>
                            <MembersLayoutToggle
                                layoutMode={layoutMode}
                                onChange={setLayoutMode}
                                className="ml-auto shrink-0"
                            />
                            <div className="flex items-end gap-1 sm:gap-2 overflow-x-auto flex-1 min-w-0 pb-0 dark:pb-0 [&>*]:flex-shrink-0">
                        {isGym() ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => { setStatusView('active'); setMembersPage(0); }}
                                    className={`flex items-center gap-1.5 min-h-[44px] rounded-xl border transition-colors ${isMobile ? 'px-3 py-2' : 'px-4 py-2'} ${
                                        statusView === 'active'
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                                            : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-blue-400 dark:hover:bg-white/5'
                                    } ${!isMobile ? 'dark:px-5 dark:py-2.5' : ''}`}
                                >
                                    <Users className="dark:hidden shrink-0" size={isMobile ? 16 : 18} />
                                    <span className="font-semibold text-sm whitespace-nowrap dark:font-open-sans">{t('gym.membersManager.statusTabs.clients')}</span>
                                    {statusView === 'active' ? (
                                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-indigo-900/50 dark:text-white">{memberTabTotals.clients}</span>
                                    ) : (
                                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{memberTabTotals.clients}</span>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setStatusView('trainer'); setMembersPage(0); }}
                                    className={`flex items-center gap-1.5 min-h-[44px] rounded-xl border transition-colors ${isMobile ? 'px-3 py-2' : 'px-4 py-2'} ${
                                        statusView === 'trainer'
                                            ? 'bg-blue-600 text-white border-blue-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                                            : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-blue-400 dark:hover:bg-white/5'
                                    } ${!isMobile ? 'dark:px-5 dark:py-2.5' : ''}`}
                                >
                                    <UserCheck className="dark:hidden shrink-0" size={isMobile ? 16 : 18} />
                                    <span className="font-semibold text-sm whitespace-nowrap dark:font-open-sans">{t('gym.membersManager.statusTabs.trainers')}</span>
                                    {statusView === 'trainer' ? (
                                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-indigo-900/50 dark:text-white">{memberTabTotals.trainers}</span>
                                    ) : (
                                        <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{memberTabTotals.trainers}</span>
                                    )}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={() => { setStatusView('active'); setMembersPage(0); }}
                                className={`flex items-center gap-1.5 min-h-[44px] rounded-xl border transition-colors ${isMobile ? 'px-3 py-2' : 'px-4 py-2'} ${
                                    statusView === 'active'
                                        ? 'bg-blue-600 text-white border-blue-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                                        : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-blue-400 dark:hover:bg-white/5'
                                } ${!isMobile ? 'dark:px-5 dark:py-2.5' : ''}`}
                            >
                                <Users className="dark:hidden shrink-0" size={isMobile ? 16 : 18} />
                                <span className="font-semibold text-sm whitespace-nowrap dark:font-open-sans">{t('gym.membersManager.statusTabs.clients')}</span>
                                {statusView === 'active' ? (
                                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-indigo-900/50 dark:text-white">{activeMembers.length}</span>
                                ) : (
                                    <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{activeMembers.length}</span>
                                )}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => { setStatusView('inactive'); setMembersPage(0); }}
                            className={`flex items-center gap-1.5 min-h-[44px] rounded-xl border transition-colors ${isMobile ? 'px-3 py-2' : 'px-4 py-2'} ${
                                statusView === 'inactive'
                                    ? 'bg-amber-600 text-white border-amber-600 shadow-md dark:bg-gray-900 dark:border-transparent dark:shadow-none dark:rounded-b-none dark:rounded-t-lg dark:font-open-sans dark:font-semibold'
                                    : 'bg-white dark:bg-transparent text-gray-700 dark:text-white border-gray-200 dark:border-transparent hover:border-amber-400 dark:hover:bg-white/5'
                            } ${!isMobile ? 'dark:px-5 dark:py-2.5' : ''}`}
                        >
                            <ArchiveRestore className="dark:hidden shrink-0" size={isMobile ? 16 : 18} />
                            <span className="font-semibold text-sm whitespace-nowrap dark:font-open-sans">{t('gym.membersManager.statusTabs.inactive')}</span>
                            {statusView === 'inactive' ? (
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-900 dark:bg-indigo-900/50 dark:text-white">{isGym() ? memberTabTotals.inactive : inactiveMembers.length}</span>
                            ) : (
                                <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-white">{isGym() ? memberTabTotals.inactive : inactiveMembers.length}</span>
                            )}
                        </button>

                        {/* Info Popover */}
                        <Popover
                            placement="bottom"
                            title={t('gym.membersManager.statusInfo.title')}
                            trigger={
                                <button
                                    type="button"
                                    className={`flex cursor-pointer items-center justify-center rounded-full border border-[#bca8d1] bg-[#e3deef] transition-colors hover:brightness-[0.97] dark:border-white/20 dark:bg-gray-800/55 dark:hover:bg-gray-700 ${isMobile ? 'min-w-[44px] min-h-[44px]' : 'h-8 w-8'}`}
                                    title={t('gym.membersManager.statusInfo.title')}
                                    aria-label={t('gym.membersManager.statusInfo.title')}
                                >
                                    <Info className="text-[#372d83] dark:text-gray-300" size={18} />
                                </button>
                            }
                            content={
                                <div className="space-y-3">
                                    <p>{t('gym.membersManager.statusInfo.description')}</p>
                                    <p className="text-sm text-[#3a3a3a]/80 dark:text-white/75">
                                        {t('gym.membersManager.statusInfo.tip')}
                                    </p>
                                </div>
                            }
                        />
                            </div>
                        </div>
                    ) : (
                        /* Desktop: titolo/descrizione a tutta larghezza (padding); tab allineate all'inizio colonna dettaglio (9/12) come la griglia sotto */
                        <>
                            <div className="px-6 pt-6 pb-4 dark:pb-0 flex flex-wrap items-start justify-between gap-4">
                                <div>
                                    <h1 className="font-bold text-gray-900 dark:text-white dark:font-open-sans text-3xl">
                                        {isTrainer() ? t('gym.membersManager.trainerTitle') : t('gym.membersManager.title')}
                                    </h1>
                                    <p className="text-gray-600 dark:text-gray-300 dark:font-open-sans dark:font-light">
                                        {isTrainer()
                                            ? t('gym.membersManager.trainerDescription')
                                            : t('gym.membersManager.description')
                                        }
                                    </p>
                                </div>
                                <MembersLayoutToggle layoutMode={layoutMode} onChange={setLayoutMode} />
                            </div>
                            <div className={isTableLayout ? 'px-6 pb-4' : 'grid grid-cols-12 w-full gap-0 mt-6 pb-4 dark:pb-0'}>
                                {!isTableLayout && <div className="col-span-3 min-w-0" aria-hidden="true" />}
                                <div className={`flex flex-wrap items-end gap-1 sm:gap-2 ${isTableLayout ? '' : 'col-span-9 pr-6'}`}>
                                    {renderDesktopStatusTabs()}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            </>
            )}

            {/* Contenuto: layout tabella | mobile | desktop classico */}
            <div className="flex-1 overflow-hidden min-h-0">
                {isTableLayout && selectedMember ? (
                    <div className="relative z-0 flex h-full flex-col overflow-hidden bg-white dark:bg-gray-800">
                        <MemberDetailView
                            member={selectedMember}
                            onBack={handleClearSelectedMember}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            workoutsRefreshKey={workoutsRefreshKey}
                            subscriptionRefreshKey={subscriptionRefreshKey}
                            onShowCreateModal={(e) => {
                                if (e?.currentTarget) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                }
                                setShowCreateModal(true);
                            }}
                            onShowAssignTemplateModal={(e) => {
                                if (e?.currentTarget) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                }
                                setShowSelectTemplateModal(true);
                            }}
                            onOpenSubscriptionModal={(mode = 'create', subscription = null, event = null) => {
                                const triggerEvent = event;
                                if (triggerEvent?.currentTarget) {
                                    const rect = triggerEvent.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                }
                                setSubscriptionModalMode(mode);
                                setSubscriptionToEdit(subscription);
                                setShowSubscriptionModal(true);
                            }}
                            onShowEditModal={(workout) => {
                                setEditingWorkout(workout);
                                setShowEditModal(true);
                            }}
                            onShowWorkoutDetail={handleShowWorkoutDetail}
                            onConvertToTemplate={(workout) => {
                                setWorkoutToConvert(workout);
                                setShowConvertToTemplateModal(true);
                            }}
                            onStatusToggle={handleStatusToggle}
                            onReactivate={handleReactivateMember}
                            statusLoadingId={statusLoadingId}
                            onRemove={handleRemoveMember}
                            onResetPassword={handleResetPassword}
                            onEditProfile={() => setShowEditProfileModal(true)}
                            onEditExtendedSection={openExtendedProfileSection}
                            onMemberUpdated={handleMemberPatch}
                            canModify={canModifyMembers}
                            canReactivate={canReactivateMembers}
                        />
                    </div>
                ) : isTableLayout ? (
                    <div className="flex h-full min-h-0 flex-col overflow-hidden px-3 pb-3 pt-2 md:px-6 md:pb-6">
                        <div className="mb-4 flex flex-wrap items-end gap-3">
                            <div className="relative min-w-[220px] flex-1">
                                <label className="pointer-events-none absolute left-2 -top-2.5 z-10 bg-gray-50 px-1.5 text-xs font-medium text-gray-500 dark:bg-gray-900 dark:text-white">
                                    {t('gym.membersManager.filters.searchLabel')}
                                </label>
                                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/50" size={18} aria-hidden />
                                <input
                                    type="text"
                                    placeholder={t('gym.membersManager.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={handleSearchTermChange}
                                    className="w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                />
                            </div>
                            {statusView === 'active' && isGym() && (
                                <>
                                    <div className="relative min-w-[160px] flex-1">
                                        <label className="pointer-events-none absolute left-2 -top-2.5 z-10 bg-gray-50 px-1.5 text-xs font-medium text-gray-500 dark:bg-gray-900 dark:text-white">
                                            {t('gym.membersManager.filters.subscriptionLabel')}
                                        </label>
                                        <select
                                            value={filterSubscription}
                                            onChange={(e) => { setFilterSubscription(e.target.value); setMembersPage(0); }}
                                            className="w-full rounded-xl border border-gray-300 py-2.5 px-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                        >
                                            <option value="">{t('gym.membersManager.filters.subscriptionAll')}</option>
                                            <option value="active">{t('gym.membersManager.memberStatus.activeSubscription')}</option>
                                            <option value="expired">{t('gym.membersManager.memberStatus.expiredSubscription')}</option>
                                            <option value="no_subscription">{t('gym.membersManager.memberStatus.noSubscription')}</option>
                                        </select>
                                    </div>
                                    <div className="relative min-w-[160px] flex-1">
                                        <label className="pointer-events-none absolute left-2 -top-2.5 z-10 bg-gray-50 px-1.5 text-xs font-medium text-gray-500 dark:bg-gray-900 dark:text-white">
                                            {t('gym.membersManager.filters.lastWorkoutLabel')}
                                        </label>
                                        <select
                                            value={filterLastWorkout}
                                            onChange={(e) => { setFilterLastWorkout(e.target.value); setMembersPage(0); }}
                                            className="w-full rounded-xl border border-gray-300 py-2.5 px-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                                        >
                                            <option value="">{t('gym.membersManager.filters.lastWorkoutAll')}</option>
                                            <option value="never">{t('gym.membersManager.filters.lastWorkoutNever')}</option>
                                            <option value="7">{t('gym.membersManager.filters.lastWorkout7')}</option>
                                            <option value="7_30">{t('gym.membersManager.filters.lastWorkout7_30')}</option>
                                            <option value="30_plus">{t('gym.membersManager.filters.lastWorkout30Plus')}</option>
                                        </select>
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="min-h-0 flex-1">
                            <MembersTableView
                                members={filteredMembers}
                                listLoading={listLoading}
                                selectedMemberId={selectedMember?.id}
                                onSelectMember={handleSelectMember}
                                statusView={statusView}
                                isGym={isGym()}
                                isTrainer={isTrainer()}
                                totalMembers={totalMembers}
                                membersPage={membersPage}
                                pageSize={MEMBERS_PAGE_SIZE}
                                onPageChange={setMembersPage}
                                emptyTitle={tableLayoutEmptyTitle}
                                emptyDescription={tableLayoutEmptyDescription}
                            />
                        </div>
                    </div>
                ) : isMobile && selectedMember ? (
                    /* Mobile: solo dettaglio a schermo intero (la barra Indietro è sopra) */
                    <div className="h-full flex flex-col bg-white dark:bg-gray-800 overflow-hidden relative z-0">
                        <MemberDetailView
                            member={selectedMember}
                            onBack={() => setSelectedMember(null)}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                        workoutsRefreshKey={workoutsRefreshKey}
                            subscriptionRefreshKey={subscriptionRefreshKey}
                            onShowCreateModal={(e) => {
                                if (e?.currentTarget) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                }
                                setShowCreateModal(true);
                            }}
                            onShowAssignTemplateModal={(e) => {
                                if (e?.currentTarget) {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                }
                                setShowSelectTemplateModal(true);
                            }}
                            onOpenSubscriptionModal={(mode = 'create', subscription = null, event = null) => {
                                const triggerEvent = event;
                                if (triggerEvent?.currentTarget) {
                                    const rect = triggerEvent.currentTarget.getBoundingClientRect();
                                    setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                }
                                setSubscriptionModalMode(mode);
                                setSubscriptionToEdit(subscription);
                                setShowSubscriptionModal(true);
                            }}
                            onShowEditModal={(workout) => {
                                setEditingWorkout(workout);
                                setShowEditModal(true);
                            }}
                            onShowWorkoutDetail={handleShowWorkoutDetail}
                            onConvertToTemplate={(workout) => {
                                setWorkoutToConvert(workout);
                                setShowConvertToTemplateModal(true);
                            }}
                            onStatusToggle={handleStatusToggle}
                            onReactivate={handleReactivateMember}
                            statusLoadingId={statusLoadingId}
                            onRemove={handleRemoveMember}
                            onResetPassword={handleResetPassword}
                            onEditProfile={() => setShowEditProfileModal(true)}
                            onEditExtendedSection={openExtendedProfileSection}
                            onMemberUpdated={handleMemberPatch}
                            canModify={canModifyMembers}
                            canReactivate={canReactivateMembers}
                        />
                    </div>
                ) : isMobile ? (
                    /* Mobile: lista a tutta larghezza + paginazione (WEBAPP-034) */
                    <div className="h-full flex flex-col bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    placeholder={t('gym.membersManager.searchPlaceholder')}
                                    value={searchTerm}
                                    onChange={handleSearchTermChange}
                                    className="w-full pl-9 pr-2.5 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-base"
                                />
                            </div>
                        </div>
                        {/* Paginazione lista clienti - mobile (WEBAPP-034) */}
                        {isGym() && totalMembers > MEMBERS_PAGE_SIZE && (
                            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-200 dark:border-white/10 dark:bg-gray-900 flex-shrink-0 min-h-[44px]">
                                <span className="text-sm text-gray-600 dark:text-white/50">
                                    {t('gym.membersManager.history.pageOf', { current: membersPage + 1, total: Math.max(1, Math.ceil(totalMembers / MEMBERS_PAGE_SIZE)) })}
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={membersPage === 0}
                                        onClick={() => setMembersPage((p) => p - 1)}
                                        className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-200 dark:bg-gray-800 dark:text-white dark:disabled:!opacity-[0.35] dark:disabled:hover:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600"
                                    >
                                        {t('gym.membersManager.history.prev')}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={membersPage >= Math.ceil(totalMembers / MEMBERS_PAGE_SIZE) - 1}
                                        onClick={() => setMembersPage((p) => p + 1)}
                                        className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed active:bg-gray-200 dark:bg-gray-800 dark:text-white dark:disabled:!opacity-[0.35] dark:disabled:hover:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600"
                                    >
                                        {t('gym.membersManager.history.next')}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div ref={membersListScrollMobileRef} className="members-list-scrollbar flex-1 overflow-y-auto min-h-0 dark:bg-gray-900">
                            {listLoading ? (
                                <div className="p-2">
                                    <SkeletonList rows={6} />
                                </div>
                            ) : filteredMembers.length > 0 ? (
                                <div className="p-2">
                                    {filteredMembers.map((member) => (
                                        <MemberCard
                                            key={member.id}
                                            member={member}
                                            isSelected={false}
                                            onClick={() => setSelectedMember(member)}
                                            viewMode={statusView}
                                            onReactivate={handleReactivateMember}
                                            canReactivate={canReactivateMembers}
                                            statusLoadingId={statusLoadingId}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-white/80 px-4">
                                    <Users size={48} className="mb-4 opacity-50 dark:text-white/50" />
                                    <p className="font-semibold">
                                        {statusView === 'inactive'
                                            ? t('gym.membersManager.emptyState.inactive')
                                            : statusView === 'trainer'
                                            ? t('gym.membersManager.emptyState.noTrainers')
                                            : t('gym.membersManager.emptyState.search')}
                                    </p>
                                    <p className="text-sm text-gray-400 dark:text-white/65 mt-2">
                                        {statusView === 'inactive'
                                            ? t('gym.membersManager.emptyState.inactiveDescription')
                                            : statusView === 'trainer'
                                            ? t('gym.membersManager.emptyState.noTrainersDescription')
                                            : t('gym.membersManager.emptyState.searchDescription')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                /* Desktop: griglia lista (col-3) + dettaglio (col-9) */
                <div className="grid grid-cols-12 gap-0 h-full dark:bg-gray-800">
                    {/* Sidebar Membri */}
                    <div className="col-span-3 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col">
                        <div className="border-b border-gray-200 dark:border-gray-700 p-4 dark:p-0">
                            <div className="space-y-4 dark:bg-gray-900 dark:px-4 dark:pt-4 dark:pb-4">
                                <div className="relative">
                                    <label className="absolute left-2 -top-2.5 px-1.5 text-xs font-medium text-gray-500 bg-white pointer-events-none z-10 dark:text-white dark:bg-gray-900">
                                        {t('gym.membersManager.filters.searchLabel')}
                                    </label>
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-white/50 pointer-events-none" size={20} aria-hidden />
                                    <input
                                        type="text"
                                        placeholder={t('gym.membersManager.searchPlaceholder')}
                                        value={searchTerm}
                                        onChange={handleSearchTermChange}
                                        className="w-full pl-10 pr-fluid-sm py-2 pt-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-fluid-base text-gray-900 bg-white placeholder:text-gray-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:placeholder:text-white/50 dark:focus:ring-white/30 dark:focus:border-indigo-500"
                                        aria-label={t('gym.membersManager.searchPlaceholder')}
                                    />
                                </div>
                            {/* WEBAPP-032: filtri sotto ricerca, stessa riga, solo desktop (non mobile) */}
                            {statusView === 'active' && isGym() && (
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="flex-1 min-w-0 relative">
                                        <label className="absolute left-2 -top-2.5 px-1.5 text-xs font-medium text-gray-500 bg-white pointer-events-none dark:text-white dark:bg-gray-900">
                                            {t('gym.membersManager.filters.subscriptionLabel')}
                                        </label>
                                        <select
                                            value={filterSubscription}
                                            onChange={(e) => { setFilterSubscription(e.target.value); setMembersPage(0); }}
                                            className="w-full text-sm border border-gray-300 rounded-md py-2 px-2 pt-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:focus:ring-white/30 dark:focus:border-indigo-500"
                                            aria-label={t('gym.membersManager.filters.subscription')}
                                        >
                                            <option value="">{t('gym.membersManager.filters.subscriptionAll')}</option>
                                            <option value="active">{t('gym.membersManager.memberStatus.activeSubscription')}</option>
                                            <option value="expired">{t('gym.membersManager.memberStatus.expiredSubscription')}</option>
                                            <option value="no_subscription">{t('gym.membersManager.memberStatus.noSubscription')}</option>
                                        </select>
                                    </div>
                                    <div className="flex-1 min-w-0 relative">
                                        <label className="absolute left-2 -top-2.5 px-1.5 text-xs font-medium text-gray-500 bg-white pointer-events-none dark:text-white dark:bg-gray-900">
                                            {t('gym.membersManager.filters.lastWorkoutLabel')}
                                        </label>
                                        <select
                                            value={filterLastWorkout}
                                            onChange={(e) => { setFilterLastWorkout(e.target.value); setMembersPage(0); }}
                                            className="w-full text-sm border border-gray-300 rounded-md py-2 px-2 pt-2.5 bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:focus:ring-white/30 dark:focus:border-indigo-500"
                                            aria-label={t('gym.membersManager.filters.lastWorkout')}
                                        >
                                            <option value="">{t('gym.membersManager.filters.lastWorkoutAll')}</option>
                                            <option value="never">{t('gym.membersManager.filters.lastWorkoutNever')}</option>
                                            <option value="7">{t('gym.membersManager.filters.lastWorkout7')}</option>
                                            <option value="7_30">{t('gym.membersManager.filters.lastWorkout7_30')}</option>
                                            <option value="30_plus">{t('gym.membersManager.filters.lastWorkout30Plus')}</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col items-end justify-center gap-0.5 ml-auto">
                                        <span className="text-xs text-gray-500 whitespace-nowrap dark:text-white">
                                            {(filterSubscription || filterLastWorkout) || (isGym() && totalMembers > 0)
                                                ? t('gym.membersManager.filters.countOf', { count: filteredMembers.length, total: isGym() && totalMembers > 0 ? totalMembers : visibleMembers.length })
                                                : t('gym.membersManager.filters.count', { count: filteredMembers.length })}
                                        </span>
                                        {(filterSubscription || filterLastWorkout) && (
                                            <button
                                                type="button"
                                                onClick={() => { setFilterSubscription(''); setFilterLastWorkout(''); setMembersPage(0); }}
                                                className="text-xs font-medium text-indigo-600 hover:underline whitespace-nowrap -mb-0.5 dark:text-white dark:hover:text-white/90"
                                            >
                                                {t('gym.membersManager.filters.clear')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                            </div>
                        </div>

                        {/* WEBAPP-034: paginazione lista clienti (desktop; mobile ha blocco dedicato sopra) */}
                        {isGym() && totalMembers > MEMBERS_PAGE_SIZE && (
                            <div className="flex items-center justify-between gap-2 px-2 py-1.5 border-t border-gray-200 dark:border-white/10 dark:bg-gray-900">
                                <span className="text-sm text-gray-600 dark:text-white/50">
                                    {t('gym.membersManager.history.pageOf', { current: membersPage + 1, total: Math.max(1, Math.ceil(totalMembers / MEMBERS_PAGE_SIZE)) })}
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        disabled={membersPage === 0}
                                        onClick={() => setMembersPage((p) => p - 1)}
                                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:disabled:!opacity-[0.35] dark:disabled:hover:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600"
                                    >
                                        {t('gym.membersManager.history.prev')}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={membersPage >= Math.ceil(totalMembers / MEMBERS_PAGE_SIZE) - 1}
                                        onClick={() => setMembersPage((p) => p + 1)}
                                        className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 dark:bg-gray-800 dark:text-white dark:disabled:!opacity-[0.35] dark:disabled:hover:bg-gray-800 dark:hover:bg-gray-700 dark:active:bg-gray-600"
                                    >
                                        {t('gym.membersManager.history.next')}
                                    </button>
                                </div>
                            </div>
                        )}

                        <div ref={membersListScrollDesktopRef} className="members-list-scrollbar overflow-y-auto flex-1 dark:bg-gray-900">
                            {listLoading ? (
                                <div className="p-2">
                                    <SkeletonList rows={8} />
                                </div>
                            ) : filteredMembers.length > 0 ? (
                                <div className="p-2">
                                    {filteredMembers.map((member) => (
                                        <MemberCard
                                            key={member.id}
                                            member={member}
                                            isSelected={selectedMember?.id === member.id}
                                            onClick={() => setSelectedMember(member)}
                                            viewMode={statusView}
                                            onReactivate={handleReactivateMember}
                                            canReactivate={canReactivateMembers}
                                            statusLoadingId={statusLoadingId}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-center text-gray-500 dark:text-white/80 px-4">
                                    <Users size={48} className="mb-4 opacity-50 dark:text-white/50" />
                                    <p className="font-semibold">
                                        {statusView === 'inactive'
                                            ? t('gym.membersManager.emptyState.inactive')
                                            : statusView === 'trainer'
                                            ? t('gym.membersManager.emptyState.noTrainers')
                                            : t('gym.membersManager.emptyState.search')}
                                    </p>
                                    <p className="text-sm text-gray-400 dark:text-white/65 mt-2">
                                        {statusView === 'inactive'
                                            ? t('gym.membersManager.emptyState.inactiveDescription')
                                            : statusView === 'trainer'
                                            ? t('gym.membersManager.emptyState.noTrainersDescription')
                                            : t('gym.membersManager.emptyState.searchDescription')}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dettaglio Membro - Sempre affiancato con scroll */}
                    <div className="col-span-9 bg-white dark:bg-gray-800 overflow-hidden">
                        {selectedMember ? (
                            <MemberDetailView
                                member={selectedMember}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                        workoutsRefreshKey={workoutsRefreshKey}
                                subscriptionRefreshKey={subscriptionRefreshKey}
                                onShowCreateModal={(e) => {
                                    if (e?.currentTarget) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                    }
                                    setShowCreateModal(true);
                                }}
                                onShowAssignTemplateModal={(e) => {
                                    if (e?.currentTarget) {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                    }
                                    setShowSelectTemplateModal(true);
                                }}
                                onOpenSubscriptionModal={(mode = 'create', subscription = null, event = null) => {
                                    const triggerEvent = event;
                                    if (triggerEvent?.currentTarget) {
                                        const rect = triggerEvent.currentTarget.getBoundingClientRect();
                                        setModalOrigin({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });
                                    }
                                    setSubscriptionModalMode(mode);
                                    setSubscriptionToEdit(subscription);
                                    setShowSubscriptionModal(true);
                                }}
                                onShowEditModal={(workout) => {
                                    setEditingWorkout(workout);
                                    setShowEditModal(true);
                                }}
                                onShowWorkoutDetail={handleShowWorkoutDetail}
                                onConvertToTemplate={(workout) => {
                                    setWorkoutToConvert(workout);
                                    setShowConvertToTemplateModal(true);
                                }}
                                onStatusToggle={handleStatusToggle}
                                onReactivate={handleReactivateMember}
                                statusLoadingId={statusLoadingId}
                                onRemove={handleRemoveMember}
                                onResetPassword={handleResetPassword}
                                onEditProfile={() => setShowEditProfileModal(true)}
                                onEditExtendedSection={openExtendedProfileSection}
                                onMemberUpdated={handleMemberPatch}
                                canModify={canModifyMembers}
                                canReactivate={canReactivateMembers}
                            />
                        ) : (
                            <div
                                className={`flex h-full flex-col items-center justify-center px-6 text-center ${
                                    statusView === 'trainer' || statusView === 'inactive'
                                        ? 'bg-white text-gray-600 dark:bg-gray-900 dark:text-white/80'
                                        : 'text-gray-500 dark:text-gray-400'
                                }`}
                            >
                                <User
                                    size={64}
                                    className={`mb-4 opacity-50 ${statusView === 'trainer' || statusView === 'inactive' ? 'text-gray-400 dark:text-white/35' : ''}`}
                                />
                                <p className="text-lg font-medium">
                                    {statusView === 'inactive'
                                        ? t('gym.membersManager.emptyState.inactive')
                                        : t('gym.membersManager.selectMember')}
                                </p>
                                {statusView === 'inactive' && (
                                    <p className="mt-2 text-sm text-gray-500 dark:text-white/60">
                                        {t('gym.membersManager.emptyState.inactiveDescription')}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                ) }
            </div>

            {/* Modal Creazione Scheda - Con Animazione iOS */}
                {selectedMember && (
                    <IOSModal
                        isOpen={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        origin={modalOrigin}
                        size="full"
                        showCloseButton={false}
                        className="!w-[90vw] !h-[90vh] !max-w-[90vw] !max-h-[90vh]"
                    >
                        <CreateWorkoutModal
                            client={selectedMember}
                            isOpen={showCreateModal}
                            onClose={() => setShowCreateModal(false)}
                            onSuccess={() => {
                                // Aggiorna solo la tab Schede: niente fetchMembers() qui (altrimenti loading=true
                                // nasconde tutta la pagina con lo spinner, effetto "refresh generale").
                                if (selectedMember && activeTab === 'workouts') {
                                    setTimeout(() => {
                                        setWorkoutsRefreshKey((k) => k + 1);
                                    }, 500);
                                }
                                setShowCreateModal(false);
                            }}
                        />
                    </IOSModal>
                )}

                {/* Modal Modifica Scheda */}
                {selectedMember && editingWorkout && (
                    <EditWorkoutModal
                        workout={editingWorkout}
                        client={selectedMember}
                        isOpen={showEditModal}
                        onClose={() => {
                            setShowEditModal(false);
                            setEditingWorkout(null);
                        }}
                        onSuccess={() => {
                            // Solo refresh lista schede in WorkoutsTab (no loading full-page).
                            setTimeout(() => {
                                setWorkoutsRefreshKey((k) => k + 1);
                            }, 500);
                            setShowEditModal(false);
                            setEditingWorkout(null);
                        }}
                    />
                )}

                {/* Modal Abbonamento - Con Animazione iOS */}
                {selectedMember && (
                    <IOSModal
                        isOpen={showSubscriptionModal}
                        onClose={() => {
                            setShowSubscriptionModal(false);
                            setSubscriptionToEdit(null);
                            setSubscriptionModalMode('create');
                        }}
                        origin={modalOrigin}
                        size="xl"
                        title={subscriptionModalMode === 'edit' ? t('gym.membersManager.subscription.editModalTitle') : t('gym.membersManager.subscription.newSubscription')}
                    >
                        <CreateSubscriptionModal
                            client={selectedMember}
                            isOpen={showSubscriptionModal}
                            onClose={() => setShowSubscriptionModal(false)}
                            mode={subscriptionModalMode}
                            initialSubscription={subscriptionToEdit}
                            onSuccess={() => {
                                setSubscriptionRefreshKey((k) => k + 1);
                                if (selectedMember?.id) {
                                    refreshMemberSubscriptionRow(selectedMember.id);
                                }
                                setShowSubscriptionModal(false);
                                setSubscriptionToEdit(null);
                                setSubscriptionModalMode('create');
                            }}
                        />
                    </IOSModal>
                )}

                {/* Modal Visualizzazione Scheda */}
                {selectedMember && selectedWorkout && (
                    <WorkoutDetailModal
                        workout={selectedWorkout}
                        client={selectedMember}
                        isOpen={showWorkoutDetailModal}
                        onClose={() => {
                            setShowWorkoutDetailModal(false);
                            setSelectedWorkout(null);
                        }}
                    />
                )}

                {/* Modal Selezione Template */}
                {selectedMember && (
                    <SelectTemplateModal
                        isOpen={showSelectTemplateModal}
                        member={selectedMember}
                        onClose={() => setShowSelectTemplateModal(false)}
                        onTemplateSelected={(template) => {
                            console.log('[MEMBERS_MANAGER] Template selected:', template);
                            setSelectedTemplateForAssign(template);
                            setShowSelectTemplateModal(false);
                            setShowAssignTemplateModal(true);
                        }}
                    />
                )}

                {/* Modal Converti Scheda in Template - WEBAPP-016 */}
                {selectedMember && workoutToConvert && (
                    <ConvertToTemplateModal
                        isOpen={showConvertToTemplateModal}
                        onClose={() => {
                            setShowConvertToTemplateModal(false);
                            setWorkoutToConvert(null);
                        }}
                        workout={workoutToConvert}
                        onSuccess={(templateId) => {
                            console.log('[WEBAPP-016] Template creato:', templateId);
                            setShowConvertToTemplateModal(false);
                            setWorkoutToConvert(null);
                            // Opzionale: Refresh lista schede
                            setTimeout(() => {
                                window.location.reload();
                            }, 1000);
                        }}
                    />
                )}

                {/* Modal Assegna Template */}
                {selectedMember && selectedTemplateForAssign && (
                    <AssignTemplateModal
                        isOpen={showAssignTemplateModal}
                        template={selectedTemplateForAssign}
                        member={selectedMember}
                        onClose={() => {
                            setShowAssignTemplateModal(false);
                            setSelectedTemplateForAssign(null);
                        }}
                        onSuccess={() => {
                            setShowAssignTemplateModal(false);
                            setSelectedTemplateForAssign(null);
                            fetchMembers();
                        }}
                    />
                )}

                {/* Modal Dati gestiti */}
                {selectedMember && (
                    <EditClientProfileModal
                        client={selectedMember}
                        isOpen={showEditProfileModal}
                        onClose={() => setShowEditProfileModal(false)}
                        onSuccess={(updatedUser) => {
                            // Aggiorna lista e selezione incluso phone (altrimenti reset password / WhatsApp non vedono il numero fino a refresh)
                            const patch = {
                                name: updatedUser.name,
                                email: updatedUser.email,
                                ...(Object.prototype.hasOwnProperty.call(updatedUser, 'phone')
                                    ? { phone: updatedUser.phone }
                                    : {}),
                            };
                            setMembers(prev => prev.map(m => 
                                m.id === updatedUser.id ? { ...m, ...patch } : m
                            ));
                            setSelectedMember(prev => (prev && prev.id === updatedUser.id ? { ...prev, ...patch } : prev));
                            
                            setShowEditProfileModal(false);
                        }}
                    />
                )}

                {selectedMember && (
                    <EditExtendedProfileModal
                        client={selectedMember}
                        section={extendedProfileSection}
                        isOpen={showExtendedProfileModal}
                        onClose={closeExtendedProfileModal}
                        onSuccess={(updatedMember) => {
                            const patch = {
                                extended_profile: updatedMember.extended_profile,
                                membership_notes: updatedMember.membership_notes,
                            };
                            setMembers((prev) => prev.map((m) => (
                                m.id === updatedMember.id ? { ...m, ...patch } : m
                            )));
                            setSelectedMember((prev) => (
                                prev && prev.id === updatedMember.id ? { ...prev, ...patch } : prev
                            ));
                            closeExtendedProfileModal();
                        }}
                    />
                )}

                {/* Modal Reset Password */}
                {/* Delete Client Modal */}
                <DeleteMemberModal
                    isOpen={showDeleteModal}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setMemberToDelete(null);
                    }}
                    onConfirm={executeDeleteMember}
                    memberName={memberToDelete?.name || ''}
                    memberEmail={memberToDelete?.email || ''}
                    loading={deleting}
                />

                <ResetPasswordModal
                    isOpen={showResetPasswordModal}
                    onClose={() => {
                        setShowResetPasswordModal(false);
                        setSelectedMemberForReset(null);
                    }}
                    member={selectedMemberForReset}
                    onResetPassword={handleResetPasswordConfirm}
                />
        </div>
    );
}

/**
 * Pallino stato cliente (lista + dettaglio): priorità «Nuovo cliente» (blu), poi stato IFR.
 * Inattivo = icona triangolo (non pallino).
 */
function getMemberStatoClienteDisplay(member, t) {
    const stato = member.stato_cliente;
    if (!stato) return null;
    if (stato === 'inattivo') {
        return { kind: 'inattivo', title: t('gym.membersManager.statoCliente.inattivo') };
    }
    if (member.nuovo_cliente === true) {
        const base = t('gym.membersManager.statoCliente.nuovoCliente', { defaultValue: 'Nuovo cliente' });
        let suffix = '';
        if (stato === 'fuori_calcolo') {
            suffix = ` (${t('gym.membersManager.statoCliente.fuoriCalcolo', { defaultValue: 'Fuori calcolo' })})`;
        } else if (stato === 'mai_allenato') {
            suffix = ` (${t('gym.membersManager.statoCliente.maiAllenato')})`;
        } else if (stato === 'attivo') {
            suffix = ` (${t('gym.membersManager.statoCliente.attivo')})`;
        } else if (stato === 'in_rallentamento') {
            suffix = ` (${t('gym.membersManager.statoCliente.inRallentamento')})`;
        } else if (stato === 'rischio_abbandono') {
            suffix = ` (${t('gym.membersManager.statoCliente.rischioAbbandono')})`;
        }
        return { kind: 'dot', dotClass: 'bg-blue-600 dark:bg-blue-400', title: base + suffix };
    }
    if (stato === 'attivo') {
        return { kind: 'dot', dotClass: 'bg-green-500', title: t('gym.membersManager.statoCliente.attivo') };
    }
    if (stato === 'mai_allenato') {
        return { kind: 'dot', dotClass: 'bg-gray-400 dark:bg-white/40', title: t('gym.membersManager.statoCliente.maiAllenato') };
    }
    if (stato === 'fuori_calcolo') {
        return { kind: 'dot', dotClass: 'bg-neutral-900 dark:bg-neutral-200', title: t('gym.membersManager.statoCliente.fuoriCalcolo', { defaultValue: 'Fuori calcolo' }) };
    }
    if (stato === 'in_rallentamento') {
        return { kind: 'dot', dotClass: 'bg-orange-500', title: t('gym.membersManager.statoCliente.inRallentamento') };
    }
    if (stato === 'rischio_abbandono') {
        return { kind: 'dot', dotClass: 'bg-red-500', title: t('gym.membersManager.statoCliente.rischioAbbandono') };
    }
    return null;
}

// Componente Card Membro
function MemberCard({ member, isSelected, onClick, viewMode, onReactivate, canReactivate, statusLoadingId }) {
    const { t } = useTranslation();
    const membershipStatus = member?.membership_status || 'active';
    const removalDate = member?.left_at ? new Date(member.left_at) : null;
    const profilePhotoUrl = member?.avatar_url || member?.profile_image_url || member?.photo_url || null;

    const getRoleText = (role) => {
        switch (role) {
            case 'trainer': return t('gym.membersManager.memberCard.trainer');
            case 'manager': return t('gym.membersManager.memberCard.manager');
            default: return t('gym.membersManager.memberCard.member');
        }
    };

    const statoClienteUi = member.role_in_gym === 'member' ? getMemberStatoClienteDisplay(member, t) : null;

    return (
        <div
            data-member-id={member.id}
            onClick={onClick}
            className={`p-3 rounded-lg cursor-pointer transition-colors mb-2 ${
                isSelected
                    ? 'bg-blue-50 border-2 border-blue-500 dark:bg-indigo-950/50 dark:border-indigo-500'
                    : 'hover:bg-gray-50 border-2 border-transparent dark:hover:bg-white/10'
            }`}
        >
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-blue-600 dark:bg-transparent ring-0">
                    {profilePhotoUrl ? (
                        <img src={profilePhotoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                        <img src={memberAvatarPlaceholder} alt="" className="w-full h-full object-cover" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                        {statoClienteUi && (
                            <span className="flex-shrink-0" title={statoClienteUi.title}>
                                {statoClienteUi.kind === 'inattivo' ? (
                                    <AlertTriangle className="w-4 h-4 text-amber-500 dark:text-amber-300" aria-hidden />
                                ) : (
                                    <span
                                        className={`w-2.5 h-2.5 rounded-full block ${statoClienteUi.dotClass}`}
                                        aria-hidden
                                    />
                                )}
                            </span>
                        )}
                        <p className="font-medium text-gray-900 dark:text-white text-sm truncate min-w-0">
                            {member.name}
                        </p>
                    </div>
                    <p className="text-gray-500 dark:text-white/75 text-xs truncate">
                        {member.email}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        {/* WEBAPP-029: badge assicurazione al posto di Cliente/Trainer; rimosso badge Attivo */}
                        {member.role_in_gym === 'member' && member.insuranceStatus != null && (
                            member.insuranceStatus === 'ok' ? (
                                <span
                                    className="inline-flex items-center justify-center shrink-0 p-0.5 rounded-full bg-green-100 dark:bg-transparent"
                                    title={t('gym.membersManager.insuranceBadge.ok')}
                                    aria-label={t('gym.membersManager.insuranceBadge.ok')}
                                >
                                    <MemberInsuranceOkIcon className="h-4 w-4 shrink-0 text-[#00cc99]" aria-hidden />
                                </span>
                            ) : (
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    member.insuranceStatus === 'scaduta'
                                        ? 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-200'
                                        : 'bg-gray-100 text-gray-800 dark:bg-white/15 dark:text-white/90'
                                }`}>
                                    {member.insuranceStatus === 'scaduta' ? t('gym.membersManager.insuranceBadge.expired') :
                                     t('gym.membersManager.insuranceBadge.none')}
                                </span>
                            )
                        )}
                        {member.role_in_gym === 'trainer' && viewMode === 'trainer' && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-white/15 dark:text-white">
                                {t('gym.membersManager.memberCard.trainer')}
                            </span>
                        )}
                        
                        {/* Indicatore stato abbonamento - solo per membri user (opzionale, lasciato per contesto) */}
                        {member.role_in_gym === 'member' && member.subscriptionStatus && (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                member.subscriptionStatus === 'active' 
                                    ? 'bg-blue-100 text-blue-800 dark:bg-teal-800 dark:text-white'
                                    : member.subscriptionStatus === 'expired'
                                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-200'
                                    : 'bg-gray-100 text-gray-800 dark:bg-white/15 dark:text-white/90'
                            }`}>
{member.subscriptionStatus === 'active' ? t('gym.membersManager.memberStatus.activeSubscription') :
                                 member.subscriptionStatus === 'expired' ? t('gym.membersManager.memberStatus.expiredSubscription') :
                                 t('gym.membersManager.memberStatus.noSubscription')}
                            </span>
                        )}
                        {/* WEBAPP-035: link statistiche individuali (stessa riga badge, a destra) */}
                        {member.role_in_gym === 'member' && (
                            <Link
                                to={`/gym/statistics-individual?memberId=${member.id}`}
                                onClick={(e) => e.stopPropagation()}
                                className="ml-auto text-xs font-medium text-indigo-600 hover:underline whitespace-nowrap dark:text-[#cda4fc] dark:hover:text-[#e4c6ff]"
                            >
                                {t('gym.membersManager.memberDetail.viewStatistics')}
                            </Link>
                        )}
                    </div>
                    {membershipStatus === 'inactive' && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                            {removalDate
                                ? t('gym.membersManager.memberCard.removedOn', { date: removalDate.toLocaleDateString('it-IT') })
                                : t('gym.membersManager.inactiveState.lastRemovalUnknown')}
                        </p>
                    )}
                </div>
            </div>
            {viewMode === 'inactive' && canReactivate && (
                <button
                    type="button"
                    onClick={(event) => {
                        event.stopPropagation();
                        if (onReactivate) {
                            onReactivate(member.id);
                        }
                    }}
                    disabled={statusLoadingId === member.id}
                    className={`mt-3 w-full flex items-center justify-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                        statusLoadingId === member.id
                            ? 'bg-amber-300 text-white cursor-not-allowed'
                            : 'bg-amber-500 hover:bg-amber-600 text-white'
                    }`}
                >
                    <ArchiveRestore size={14} />
                    {statusLoadingId === member.id
                        ? t('gym.membersManager.actions.reactivating')
                        : t('gym.membersManager.actions.reactivate')}
                </button>
            )}
        </div>
    );
}

// Barra indietro (layout tabella / mobile full screen)
function MemberDetailBackBar({ onBack, memberName, t }) {
    if (!onBack) return null;
    return (
        <div className="flex-shrink-0 flex items-center gap-3 border-b border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-white/10 dark:bg-gray-950 md:px-6 md:py-3 lg:px-8 xl:px-10">
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBack(); }}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:border-indigo-300 hover:bg-indigo-50 dark:border-white/10 dark:bg-gray-900 dark:text-white dark:hover:border-indigo-500/40 dark:hover:bg-indigo-950/40"
            >
                <ArrowLeft size={18} className="shrink-0" aria-hidden />
                {t('gym.membersManager.backToList')}
            </button>
            {memberName ? (
                <span className="hidden truncate text-sm text-gray-500 dark:text-white/50 sm:inline">
                    {memberName}
                </span>
            ) : null}
        </div>
    );
}

function MemberDetailTabs({ tabs, activeTab, setActiveTab, isMobile, fullPage = false }) {
    const navPadding = fullPage
        ? 'px-4 py-3 md:px-6 lg:px-8 xl:px-10'
        : (isMobile ? 'px-2 py-2.5' : 'px-4 py-3 md:px-6');
    return (
        <div className="flex-shrink-0 overflow-x-auto border-b border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900">
            <nav
                className={`flex w-full min-w-0 flex-nowrap gap-2 ${navPadding}`}
                aria-label="Tabs"
            >
                {tabs.map((tab) => {
                    const TabIcon = tab.Icon;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`inline-flex min-h-[40px] flex-shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-semibold transition-all md:px-4 ${
                                activeTab === tab.id
                                    ? 'border-indigo-600 bg-indigo-600 text-white shadow-sm dark:border-indigo-500 dark:bg-indigo-600'
                                    : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-indigo-200 hover:bg-indigo-50/80 hover:text-gray-900 dark:border-white/10 dark:bg-gray-800 dark:text-white/75 dark:hover:border-indigo-500/30 dark:hover:bg-indigo-950/30 dark:hover:text-white'
                            }`}
                        >
                            <TabIcon className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                            <span className="whitespace-nowrap">{tab.name}</span>
                        </button>
                    );
                })}
            </nav>
        </div>
    );
}

// Componente Vista Dettaglio Membro (onBack: layout tabella / mobile full screen)
function MemberDetailView({ member, onBack, activeTab, setActiveTab, workoutsRefreshKey, subscriptionRefreshKey, onShowCreateModal, onShowAssignTemplateModal, onOpenSubscriptionModal, onShowEditModal, onShowWorkoutDetail, onConvertToTemplate, onStatusToggle, onReactivate, statusLoadingId, onRemove, onResetPassword, onEditProfile, onEditExtendedSection, onMemberUpdated, canModify, canReactivate }) {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 1023px)'); // layout compatto solo mobile (WEBAPP-033)

    // Logica condizionale: se è trainer, mostra solo modifica/cancellazione
    if (member.role_in_gym === 'trainer') {
        return (
            <div className="h-full flex flex-col">
                <MemberDetailBackBar onBack={onBack} memberName={member.name} t={t} />
                {/* Header Membro */}
                <div className="p-6 border-b border-gray-200 dark:border-white/10 dark:bg-gray-900">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-xl">
                                {member.name.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {member.name}
                            </h2>
                            <p className="text-gray-600 dark:text-white/85">
                                {member.email}
                            </p>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-[#9ea4c6] dark:font-open-sans dark:font-light">
                                <div className="flex items-center gap-1">
                                    <Calendar size={14} className="shrink-0 text-gray-500 dark:text-[#9ea4c6]" />
                                    {t('gym.membersManager.memberDetail.registered')} {new Date(member.joined_at).toLocaleDateString('it-IT')}
                                </div>
                                <div className="flex items-center gap-1">
                                    <Clock size={14} className="shrink-0 text-gray-500 dark:text-[#9ea4c6]" />
                                    {t('gym.membersManager.memberDetail.lastAccess')} {member.last_login ? 
                                        new Date(member.last_login).toLocaleDateString('it-IT') : 
                                        t('gym.membersManager.memberDetail.never')
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Azioni per Trainer */}
                <div className="flex-1 overflow-y-auto bg-white p-6 dark:bg-gray-900">
                    <div className="text-center py-12">
                        <div className="w-24 h-24 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <User size={32} className="text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            Gestione Trainer
                        </h3>
                        <p className="mb-6 text-gray-600 dark:text-white/75">
                            Per i trainer sono disponibili solo le operazioni di modifica e rimozione
                        </p>
                        
                        {canModify && (
                            <div className="flex justify-center gap-4">
                                <button
                                    onClick={() => onStatusToggle(member.id, member.membership_status)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                                        member.membership_status === 'active'
                                            ? 'bg-red-600 hover:bg-red-700 text-white'
                                            : 'bg-green-600 hover:bg-green-700 text-white'
                                    }`}
                                >
                                    {member.membership_status === 'active' ? (
                                        <>
                                            <UserX size={16} />
                                            Disattiva
                                        </>
                                    ) : (
                                        <>
                                            <UserCheck size={16} />
                                            Attiva
                                        </>
                                    )}
                                </button>
                                
                                <button
                                    onClick={() => onResetPassword(member.id, member.name)}
                                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
                                >
                                    <User size={16} />
                                    Reset Password
                                </button>
                                
                                <button
                                    onClick={() => onRemove(member.id, member.name)}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                                >
                                    <Trash2 size={16} />
                                    Rimuovi
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    if (member.membership_status === 'inactive') {
        const removalDate = member?.left_at ? new Date(member.left_at).toLocaleDateString('it-IT') : null;

        return (
            <div className="h-full flex flex-col">
                <MemberDetailBackBar onBack={onBack} memberName={member.name} t={t} />
                <div className="p-6 border-b border-gray-200 dark:border-white/10 dark:bg-gray-900">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-xl">
                                {member.name.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {member.name}
                            </h2>
                            <p className="text-gray-600 dark:text-white/85">
                                {member.email}
                            </p>
                            <div className="flex items-center gap-2 mt-3 flex-wrap">
                                <Badge variant="warning">{t('gym.membersManager.memberStatus.inactive')}</Badge>
                                {removalDate && (
                                    <span className="text-sm text-gray-500 dark:text-[#9ea4c6] dark:font-open-sans dark:font-light">
                                        {t('gym.membersManager.inactiveState.lastRemoval', { date: removalDate })}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

            <div className="flex-1 space-y-6 overflow-y-auto bg-white p-6 dark:bg-gray-900">
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-5">
                        <div className="flex gap-3 items-start">
                            <AlertTriangle className="text-amber-600 mt-0.5" size={22} />
                            <div>
                                <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
                                    {t('gym.membersManager.inactiveState.title')}
                                </h3>
                                <p className="text-sm text-amber-800 dark:text-amber-200">
                                    {t('gym.membersManager.inactiveState.description')}
                                </p>
                                <ul className="mt-3 space-y-1 text-sm text-amber-800 dark:text-amber-100">
                                    <li>• {t('gym.membersManager.inactiveState.points.one')}</li>
                                    <li>• {t('gym.membersManager.inactiveState.points.two')}</li>
                                </ul>
                            </div>
                        </div>
                        {canReactivate && (
                            <div className="mt-4">
                                <button
                                    type="button"
                                    onClick={() => onReactivate && onReactivate(member.id)}
                                    disabled={statusLoadingId === member.id}
                                    className={`inline-flex items-center gap-2 px-5 py-2 rounded-xl font-semibold transition-colors ${
                                        statusLoadingId === member.id
                                            ? 'bg-amber-300 text-white cursor-not-allowed'
                                            : 'bg-amber-600 hover:bg-amber-700 text-white'
                                    }`}
                                >
                                    <ArchiveRestore size={16} />
                                    {statusLoadingId === member.id
                                        ? t('gym.membersManager.actions.reactivating')
                                        : t('gym.membersManager.inactiveState.action')}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-800">
                            <p className="text-sm text-gray-500 dark:text-white/65">{t('gym.membersManager.memberDetail.registered')}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {new Date(member.joined_at).toLocaleDateString('it-IT')}
                            </p>
                        </div>
                        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-gray-800">
                            <p className="text-sm text-gray-500 dark:text-white/65">{t('gym.membersManager.inactiveState.lastRemovalLabel')}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">
                                {removalDate || t('gym.membersManager.inactiveState.lastRemovalUnknown')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Per i membri user, mostra la gestione completa
    const tabs = [
        { id: 'profile', name: t('gym.membersManager.memberDetail.tabs.profile'), Icon: User },
        { id: 'anamnesis', name: t('gym.membersManager.memberDetail.tabs.anamnesis'), Icon: HeartPulse },
        { id: 'workouts', name: t('gym.membersManager.memberDetail.tabs.workouts'), Icon: MemberTabWorkoutsIcon },
        { id: 'courses', name: t('gym.membersManager.memberDetail.tabs.courses'), Icon: GraduationCap },
        { id: 'history', name: t('gym.membersManager.memberDetail.tabs.history'), Icon: MemberTabHistoryIcon },
        { id: 'subscription', name: t('gym.membersManager.memberDetail.tabs.subscription'), Icon: MemberTabSubscriptionIcon },
        { id: 'communications', name: t('gym.membersManager.memberDetail.tabs.communications'), Icon: MemberTabContactIcon },
        { id: 'consents', name: t('gym.membersManager.memberDetail.tabs.consents'), Icon: ClipboardCheck },
        { id: 'account', name: t('gym.membersManager.memberDetail.tabs.account'), Icon: Shield },
    ];

    const profilePhotoUrl = member?.avatar_url || member?.profile_image_url || member?.photo_url || null;
    const statoClienteUi = member.role_in_gym === 'member' ? getMemberStatoClienteDisplay(member, t) : null;
    const isFullPage = Boolean(onBack);

    const memberAvatarBlock = (sizeClass) => (
        <div className={`relative flex-shrink-0 ${sizeClass}`}>
            <div className="h-full w-full overflow-hidden rounded-full bg-blue-600 dark:bg-transparent">
                {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                    <img src={memberAvatarPlaceholder} alt="" className="h-full w-full object-cover" />
                )}
            </div>
            {statoClienteUi && (
                <span
                    className="absolute z-10 -top-0.5 -right-0.5 flex items-center justify-center rounded-full bg-white shadow-sm ring-2 ring-white dark:bg-gray-900 dark:ring-gray-900"
                    title={statoClienteUi.title}
                >
                    {statoClienteUi.kind === 'inattivo' ? (
                        <AlertTriangle className={`text-amber-500 ${isMobile ? 'h-3 w-3' : 'h-4 w-4'}`} aria-hidden />
                    ) : (
                        <span
                            className={`block rounded-full ${isMobile ? 'h-2 w-2' : 'h-2.5 w-2.5'} ${statoClienteUi.dotClass}`}
                            aria-hidden
                        />
                    )}
                </span>
            )}
        </div>
    );

    const memberMetaRow = (
        <div className={`flex flex-wrap items-center gap-x-5 gap-y-1 ${isMobile ? 'gap-x-2 text-[11px] text-gray-500 w-full' : 'text-sm text-gray-500'} dark:text-[#9ea4c6] dark:font-open-sans dark:font-light`}>
            <div className="flex items-center gap-1.5">
                <Calendar size={isMobile ? 12 : 14} className="shrink-0 text-gray-500 dark:text-[#9ea4c6]" />
                {t('gym.membersManager.memberDetail.registered')} {new Date(member.joined_at).toLocaleDateString('it-IT')}
            </div>
            <div className="flex items-center gap-1.5">
                <Clock size={isMobile ? 12 : 14} className="shrink-0 text-gray-500 dark:text-[#9ea4c6]" />
                {t('gym.membersManager.memberDetail.lastAccess')} {member.last_login ?
                    new Date(member.last_login).toLocaleDateString('it-IT') :
                    t('gym.membersManager.memberDetail.never')}
            </div>
        </div>
    );

    return (
        <div className={`flex flex-col ${isMobile ? 'h-full min-h-0' : 'h-full'}`}>
            <MemberDetailBackBar onBack={onBack} memberName={member.name} t={t} />
            {/* Header Membro */}
            <div className={`flex-shrink-0 border-b border-gray-200 dark:border-white/10 dark:bg-gray-900 ${
                isFullPage && !isMobile ? 'px-6 py-5 lg:px-8 xl:px-10' : (isMobile ? 'p-2' : 'p-6')
            }`}>
                {isFullPage && !isMobile ? (
                    <div className="flex w-full items-center gap-6">
                        {memberAvatarBlock('h-20 w-20 lg:h-24 lg:w-24')}
                        <div className="flex min-w-0 flex-1 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                            <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                    <h2 className="truncate text-2xl font-bold text-gray-900 dark:text-white lg:text-3xl">
                                        {member.name}
                                    </h2>
                                </div>
                                <p className="mt-1 truncate text-base text-gray-600 dark:text-white/85">{member.email}</p>
                            </div>
                            {memberMetaRow}
                        </div>
                    </div>
                ) : (
                <div className={`flex gap-4 ${isMobile ? 'flex-row items-center flex-wrap gap-y-2' : 'items-center'}`}>
                    <div className={`flex min-w-0 ${isMobile ? 'flex-row items-center gap-2 flex-1 min-w-0' : 'items-center gap-4'}`}>
                        {memberAvatarBlock(isMobile ? 'h-10 w-10' : 'h-16 w-16')}
                        <div className="flex-1 min-w-0">
                            <div className={isMobile ? 'flex flex-row flex-wrap items-center gap-x-2 gap-y-1 min-w-0' : 'flex items-center justify-between flex-wrap gap-2'}>
                            <div className={isMobile ? 'flex items-center gap-2 min-w-0 flex-shrink flex-wrap' : ''}>
                                <div className={`flex items-center gap-2 flex-wrap ${isMobile ? 'gap-1 flex-shrink-0' : ''}`}>
                                    <h2 className={`font-bold text-gray-900 dark:text-white truncate ${isMobile ? 'text-sm leading-tight' : 'text-2xl'}`}>
                                        {member.name}
                                    </h2>
                                </div>
                                <p className={`text-gray-600 dark:text-white/85 truncate ${isMobile ? 'text-xs' : ''}`}>
                                    {member.email}
                                </p>
                            </div>
                        </div>
                        {memberMetaRow}
                        </div>
                    </div>
                </div>
                )}
            </div>

            <MemberDetailTabs
                tabs={tabs}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                isMobile={isMobile}
                fullPage={isFullPage}
            />

            {/* Tab Content */}
            <div className={`flex min-h-0 flex-1 w-full flex-col overflow-y-auto bg-white dark:bg-gray-900 ${
                isFullPage ? 'p-4 md:p-6 lg:p-8 xl:px-10' : 'p-3 md:p-6'
            }`}>
                {activeTab === 'profile' && (
                    <MemberProfileTab
                        member={member}
                        canModify={canModify}
                        onEditProfile={onEditProfile}
                        onEditExtendedSection={onEditExtendedSection}
                    />
                )}
                {activeTab === 'anamnesis' && (
                    <MemberAnamnesisTab
                        member={member}
                        canModify={canModify}
                        onGoToConsents={() => setActiveTab('consents')}
                    />
                )}
                {activeTab === 'workouts' && (
                    <MemberWorkoutsTab
                        member={member}
                        workoutsRefreshKey={workoutsRefreshKey}
                        onShowCreateModal={onShowCreateModal}
                        onShowAssignTemplateModal={onShowAssignTemplateModal}
                        onShowEditModal={onShowEditModal}
                        onShowWorkoutDetail={onShowWorkoutDetail}
                        onConvertToTemplate={onConvertToTemplate}
                    />
                )}
                {activeTab === 'courses' && <MemberCoursesTab member={member} />}
                {activeTab === 'history' && <MemberHistoryTab member={member} />}
                {activeTab === 'subscription' && (
                    <MemberSubscriptionTab
                        member={member}
                        subscriptionRefreshKey={subscriptionRefreshKey}
                        onOpenSubscriptionModal={onOpenSubscriptionModal}
                        canModify={canModify}
                    />
                )}
                {activeTab === 'communications' && (
                    <MemberCommunicationsTab
                        memberId={member.id}
                        memberEmail={member.email}
                        memberPhone={member.phone}
                        memberName={member.name}
                    />
                )}
                {activeTab === 'consents' && (
                    <MemberConsentsTab
                        member={member}
                        canModify={canModify}
                        onMemberUpdated={onMemberUpdated}
                    />
                )}
                {activeTab === 'account' && (
                    <MemberAccountTab
                        member={member}
                        canModify={canModify}
                        onEditProfile={onEditProfile}
                        onResetPassword={onResetPassword}
                        onRemove={onRemove}
                    />
                )}
            </div>
        </div>
    );
}
